import type {
    CreateExtArgs,
    DalValue,
    DmApis,
    Extension,
    ExtensionAPI,
    InitializeExtArgs,
    ValidateValue
} from '@wix/document-manager-core'
import type {SchemaExtensionAPI} from '../schema/schema'
import type {RMApi} from '../rendererModel'

import type {BuilderTypes, CompRef, Pointer} from '@wix/document-services-types'
import type {PlatformExtensionAPI} from '../platform'

import {builderTypes} from '../../utils/builderUtils'
import {getComponentType} from '../../utils/dalUtils'
import type {HooksExtensionApi} from '../hooks/hooks'
import {type DataUpdateAfterEvent, DATA_MODEL_HOOKS, type DataUpdateBeforeEvent} from '../dataModel/hooks'
import {
    transformBuilderDataToFlat,
    transformBuilderDataToNestedObjects,
    BUILDER_COMPONENTS_HOOKS,
    type AppManifestUpdatedEvent
} from './builderComponentsHooks'
import {builderComponentsContainerPopulationHook} from './hooks/autoPopulateAdd'
import {builderComponentsDataUpdateHook} from './hooks/autoPopulateUpdate'
import {builderComponentsDataUpdateHookOld} from './hooks/autoPopulateUpdateOld'

import {type ComponentAfterAddDataEvent, COMPONENTS_HOOKS} from '../components/hooks'
import type {DistributorExtensionAPI} from '../distributor/distributor'
import {MANIFEST_UPDATE_MESSAGE, type SchemaResourceLoaderExtensionAPI} from '../schemaResourceLoader'
import type {ManifestUpdateMessage} from '../distributor/messages'
import {NEW_MODEL_WHITELISTED_COMPONENTS, RESOURCE_TYPES, SYSTEM_CONTAINER_TYPE} from '../../constants/constants'
import {builderComponentsRemoveSlotHook} from './hooks/autoRemoveSlot'
import {isStructurePointer} from '../structure'

interface BuilderComponentsApi extends ExtensionAPI {
    getBuilderTypes(compPointer: CompRef): BuilderTypes
    isBuilder(compType: string): boolean
    isSystemContainer(compPointer: CompRef): boolean
    getSystemContainerOfContainer(compPointer: CompRef): CompRef | undefined
}

interface BuilderComponentsExtensionAPI extends ExtensionAPI {
    builderComponents: BuilderComponentsApi
}

const createExtension = (): Extension => {
    return {
        name: 'builderComponents',
        dependencies: new Set(['rendererModel', 'serviceTopology', 'manifest']),
        createExtensionAPI({dal, extensionAPI, pointers}: CreateExtArgs): BuilderComponentsExtensionAPI {
            const {schemaAPI} = extensionAPI as SchemaExtensionAPI & SchemaResourceLoaderExtensionAPI
            const {platform} = extensionAPI as PlatformExtensionAPI

            const isSystemContainer = (compPointer: CompRef): boolean => {
                const compType = getComponentType(dal, compPointer)
                return compType === SYSTEM_CONTAINER_TYPE
            }

            return {
                builderComponents: {
                    getBuilderTypes(compPointer: CompRef): BuilderTypes {
                        const compType = getComponentType(dal, compPointer)
                        return builderTypes(schemaAPI, compType)
                    },
                    isBuilder(compType: string) {
                        return !!platform.getBuilderCompAppData(compType)
                    },
                    isSystemContainer,
                    getSystemContainerOfContainer(compPointer: CompRef): CompRef | undefined {
                        if (isSystemContainer(compPointer)) {
                            return compPointer
                        }

                        const children = pointers.structure.getChildren(compPointer) as CompRef[]
                        return children.find(child => isSystemContainer(child))
                    }
                }
            }
        },

        initializeChannelSubscriptions: async ({extensionAPI, resourceLoader}: DmApis) => {
            const {distributor, rendererModel, hooks} = extensionAPI as DistributorExtensionAPI &
                RMApi &
                HooksExtensionApi
            distributor.subscribeToMessage<ManifestUpdateMessage>(MANIFEST_UPDATE_MESSAGE, async ({data}) => {
                if (!data?.appId) {
                    throw new Error('appId should be provided')
                }
                await rendererModel.refreshClientSpecMap()
                await Promise.all([
                    resourceLoader.load(RESOURCE_TYPES.SCHEMA, [data.appId], true),
                    resourceLoader.load(RESOURCE_TYPES.COMPONENTS, [data.appId], true)
                ])
                hooks.executeHook(
                    BUILDER_COMPONENTS_HOOKS.HOT_RELOAD.APP_MANIFEST_UPDATED.createEvent({
                        appId: data.appId
                    } as AppManifestUpdatedEvent)
                )
                console.log('App was updated via wix cli hot reload')
            })
        },

        initialize: async (extArgs: InitializeExtArgs) => {
            const {extensionAPI, coreConfig} = extArgs
            const {hooks} = extensionAPI as HooksExtensionApi
            const {experimentInstance} = coreConfig

            hooks.registerHookWithExtensionArgs(
                DATA_MODEL_HOOKS.SERIALIZE.DATA_ITEM_AFTER.id,
                transformBuilderDataToFlat
            )

            hooks.registerHookWithExtensionArgs(
                DATA_MODEL_HOOKS.DESERIALIZE.DATA_ITEM_NEST_BEFORE.id,
                transformBuilderDataToNestedObjects
            )

            hooks.registerHookWithExtensionArgs(
                COMPONENTS_HOOKS.ADD_COMPONENT.AFTER.id,
                (_extArgs, {compToAddPointer}: ComponentAfterAddDataEvent) => {
                    return builderComponentsContainerPopulationHook(extArgs, compToAddPointer)
                }
            )

            if (experimentInstance.isOpen('dm_refactorStaticSlotToDynamic')) {
                hooks.registerHookWithExtensionArgs(
                    DATA_MODEL_HOOKS.DATA.UPDATE_BEFORE.id,
                    (_extArgs, {componentPointer, dataItem}: DataUpdateBeforeEvent) => {
                        return builderComponentsRemoveSlotHook(extArgs, componentPointer, dataItem)
                    }
                )
                hooks.registerHookWithExtensionArgs(
                    DATA_MODEL_HOOKS.DATA.UPDATE_AFTER.id,
                    (_extArgs, {componentPointer, dataItem}: DataUpdateAfterEvent) => {
                        return builderComponentsDataUpdateHook(extArgs, componentPointer, dataItem)
                    }
                )
            } else {
                hooks.registerHookWithExtensionArgs(
                    DATA_MODEL_HOOKS.DATA.UPDATE_BEFORE.id,
                    (_extArgs, {componentPointer, dataItem}: DataUpdateBeforeEvent) => {
                        return builderComponentsDataUpdateHookOld(extArgs, componentPointer, dataItem)
                    }
                )
            }
        },

        createValidator: (createExtArgs: CreateExtArgs): Record<string, ValidateValue> => {
            const {builderComponents, rendererModel} = createExtArgs.extensionAPI as BuilderComponentsExtensionAPI &
                RMApi
            const {experimentInstance} = createExtArgs.coreConfig

            return {
                validateNewModelCompatibility: (pointer: Pointer, value: DalValue) => {
                    if (!rendererModel.isBuilderComponentModel() || !value || !isStructurePointer(pointer)) {
                        return
                    }

                    const {componentType} = value
                    if (
                        !builderComponents.isBuilder(componentType) &&
                        !Object.values(NEW_MODEL_WHITELISTED_COMPONENTS).includes(componentType)
                    ) {
                        return [
                            {
                                shouldFail: experimentInstance.isOpen('dm_newModelCompatibilityValidation'),
                                type: 'nonCompatibleComponent',
                                message: `${value.componentType} is not whitelisted for the new model`,
                                extras: {id: pointer.id}
                            }
                        ]
                    }
                }
            }
        }
    }
}

export {createExtension, BuilderComponentsApi, BuilderComponentsExtensionAPI}
